// @ts-strict-ignore
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { uniqueId } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { injectReloadCurrentRoute } from '../functions/inject-functions/inject-reload-current-route';
import { injectSupportEmail } from '../functions/inject-functions/inject-support-email';
import { injectUntilDestroyed } from '../functions/inject-functions/inject-until-destroyed';
import { UI_STRINGS } from '../functions/language/uistrings';
import { UserAttention } from '../model/enums/user-attention';
import {
  CloseInformationDialogData,
  InformationDialogComponent,
  OpenInformationDialogData,
} from '../modules/shared/ui-kit/dialog/components/information-dialog/information-dialog.component';
import { DialogService } from '../modules/shared/ui-kit/dialog/dialog.service';
import { ToastData } from '../modules/shared/ui-kit/toast/toast.component.types';
import { ErrorType } from './error-handler.service';
import { TranslationService } from './translation-service';
import { WINDOW } from './window.service';

@Injectable()
export class MessageService {
  private _navigationError: NavigationError;
  private _supportEmail = injectSupportEmail();
  private _toastDataEmitter: Subject<ToastData> = new Subject();

  get toastDataEmitter(): Observable<ToastData> {
    return this._toastDataEmitter.asObservable();
  }

  constructor(
    private _dialogService: DialogService,
    private _router: Router,
    private _translationService: TranslationService,
    @Inject(WINDOW) private _window: Window
  ) {
    this._router.events.pipe(this._untilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationError) {
        this._navigationError = event;
      } else if (event instanceof NavigationEnd) {
        this._navigationError = undefined;
      }
    });
  }

  openErrorDialog(error: HttpErrorResponse & { errorType?: ErrorType }): void {
    const dialogData: OpenInformationDialogData = this._translationService.isUserOrDefaultLanguageLoaded()
      ? {
          type: 'error',
          title: this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.FAILED_TO_LOAD_DATA),
          text: this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.REFRESH_OR_CONTACT_SUPPORT, this._supportEmail()),
          acceptLabel: this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.REFRESH_PAGE),
          rejectLabel: this._translate(UI_STRINGS.GLOBAL.CLOSE),
          ignoreReadOnly: true,
        }
      : {
          type: 'error',
          title: 'Failed to load necessary data.',
          text: 'Please refresh the page to try again. If the error persists, please contact Platform support and provide the following information:',
          acceptLabel: 'Refresh page',
          rejectLabel: 'Close',
          ignoreReadOnly: true,
        };

    let showDetalis = true;
    let preserveTitleFormatting = false;

    switch (error.errorType) {
      case ErrorType.VARIABLES_LOAD:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.PARAMETERS_LOAD_ERROR_TITLE);
        break;
      case ErrorType.VARIABLE_SAVE:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.PARAMETER_SAVE_ERROR_TITLE);
        break;
      case ErrorType.BOARD_LOAD:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.BOARD_LOAD_ERROR_TITLE);
        break;
      case ErrorType.BOARD_SAVE:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.BOARD_SAVE_ERROR_TITLE);
        break;
      case ErrorType.REDUCTION_GOAL_SAVE:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.REDUCTION_GOAL_SAVE_ERROR_TITLE);
        break;
      case ErrorType.MIGRATION_STATUS_MISMATCH:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.MIGRATION_STATUS_MISMATCH_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.MIGRATION_STATUS_MISMATCH_ERROR_TEXT);
        showDetalis = false;
        preserveTitleFormatting = true;
        break;
      case ErrorType.MIGRATION_LOCK:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.MIGRATION_LOCK_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.MIGRATION_LOCK_ERROR_TEXT);
        showDetalis = false;
        preserveTitleFormatting = true;
        break;
      case ErrorType.INVENTORY_EXPORT:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORY_EXPORT_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORY_EXPORT_ERROR_TEXT);
        preserveTitleFormatting = true;
        dialogData.acceptLabel = this._translate(UI_STRINGS.GLOBAL.CLOSE);
        dialogData.rejectLabel = undefined;
        break;
      case ErrorType.INVENTORY_IMPORT:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORY_IMPORT_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORY_IMPORT_ERROR_TEXT);
        preserveTitleFormatting = true;
        dialogData.acceptLabel = this._translate(UI_STRINGS.GLOBAL.CLOSE);
        dialogData.rejectLabel = undefined;
        break;
      case ErrorType.INVENTORIES_PROCESSING:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORIES_PROCESSING_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INVENTORIES_PROCESSING_ERROR_TEXT);
        showDetalis = false;
        dialogData.type = 'information';
        break;
      case ErrorType.INTERVENTION_CREATE:
        dialogData.title = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INTERVENTION_CREATE_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.AUTH_ERROR_MESSAGES.INTERVENTION_CREATE_ERROR_TEXT);
        dialogData.acceptLabel = this._translate(UI_STRINGS.GLOBAL.CLOSE);
        dialogData.rejectLabel = undefined;
        break;
      case ErrorType.INTERVENTION_SHARE:
        dialogData.title = this._translate(UI_STRINGS.INTERVENTIONS.SHARE_ERROR_TITLE);
        dialogData.text = this._translate(UI_STRINGS.INTERVENTIONS.SHARE_ERROR_DESCRIPTION);
        dialogData.acceptLabel = this._translate(UI_STRINGS.GLOBAL.CLOSE);
        dialogData.rejectLabel = undefined;
        break;
    }

    const details = showDetalis ? `Error: ${error.message}\n\nMessage: ${error.error?.message}` : undefined;

    this._openDialog({
      ...dialogData,
      details,
      preserveTitleFormatting,
    })
      .pipe(this._untilDestroyed())
      .subscribe((didAccept) => {
        const shouldRefresh =
          didAccept && error.errorType !== ErrorType.INVENTORY_EXPORT && error.errorType !== ErrorType.INVENTORY_IMPORT;
        if (shouldRefresh) {
          this._onRefreshPage();
        }
      });
  }

  openUserAttentionDialog(userAttention: UserAttention): Observable<CloseInformationDialogData> {
    let DIALOGUE_STRINGS: { [key: string]: string };

    switch (userAttention) {
      case UserAttention.UnsavedData:
        DIALOGUE_STRINGS = UI_STRINGS.USER_EDITED_WARNING_DIALOGUE;
        break;
      case UserAttention.NonSavableData:
        DIALOGUE_STRINGS = UI_STRINGS.NON_SAVABLE_DATA_WARNING_DIALOGUE;
        break;
      case UserAttention.Connecting:
        DIALOGUE_STRINGS = UI_STRINGS.CONNECTING_WARNING_DIALOGUE;
        break;
      default:
        return;
    }

    return this._openDialog({
      type: 'warning',
      title: this._translate(DIALOGUE_STRINGS.TITLE),
      text: this._translate(DIALOGUE_STRINGS.TEXT),
      acceptLabel: this._translate(DIALOGUE_STRINGS.LEAVE_BUTTON_TEXT),
      rejectLabel: this._translate(UI_STRINGS.GLOBAL.CANCEL),
    });
  }

  openToast(toastData: ToastData): void {
    this._toastDataEmitter.next(toastData);
  }

  private _openDialog(dialogData: OpenInformationDialogData): Observable<CloseInformationDialogData> {
    return this._dialogService.open(InformationDialogComponent, {
      data: {
        ...dialogData,
        id: uniqueId('alertdialog'),
        role: 'alertdialog',
      },
      stack: true,
    });
  }

  private _onRefreshPage(): void {
    const currentUrl = this._router.url;
    const cacheBuster = new Date().getTime(); // Generate a unique cache-busting parameter
    const urlWithCacheBuster = `${currentUrl}?cacheBuster=${cacheBuster}`;
    this._window.location.href = urlWithCacheBuster;
    /**
     * Removed for now to implement quick cacheBusting fix
     *  if (this._navigationError) {
     *    this._router.navigateByUrl(this._navigationError.url); // If navigation occured, try navigating to the target url again
     *  } else {
     *    this._reloadCurrentRoute(); // If no navigation occured, reload current url
     *  }
     */
  }

  private _reloadCurrentRoute: () => void = injectReloadCurrentRoute();

  private _translate(stringId: string, ...params: string[]): string {
    return this._translationService.translateToUserLanguage(stringId, ...params);
  }

  private _untilDestroyed = injectUntilDestroyed();
}
